// src/data/unitsData.js

import { 
    FaSchool, FaExclamationTriangle, FaStore, FaClipboardCheck,
    FaPenFancy, FaSearch, FaUtensils, FaHistory, FaGraduationCap,
    FaTools, FaNewspaper, FaExchangeAlt, FaBookOpen, FaIndustry,
    FaMicrophone, FaMask, FaRunning, FaGavel, FaStethoscope, 
    FaHardHat, FaPalette, FaChalkboardTeacher, FaRobot, FaQuestionCircle,
    FaStar, FaUserSecret, FaRocket, FaGlobe, FaUserAstronaut, FaFileAlt,
    FaConciergeBell, FaTruck, FaBroom, FaPlane, FaLaptop, FaLeaf,
    FaMedkit, FaBriefcase, FaCogs, FaAtom, FaGem, FaFingerprint,
    FaUniversity, FaClipboardList, FaMoneyBillWave, FaHotel, FaCar,
    FaPaintRoller, FaUserTie, FaChartLine, FaCalculator, FaPercentage, FaChartBar
} from 'react-icons/fa';

// Last Updated: Wednesday, June 3, 2026

export const COURSE_UNITS_DATA = {

    // ==========================================
    // 0. PRE-BEGINNER COURSE (Level 0)
    // ==========================================
    '0_Interchange_Level_Zero': {
        category: 'General English',
        title: 'Interchange Level Zero (Foundations)',
        description: 'Beginner level for students new to English focusing on foundational vocabulary.',
        image: 'https://placehold.co/400x200/f43f5e/ffffff?text=Pre+Beginner', 
        units: [
            {
                unitId: 'pre_beg_unit_1',
                title: 'Lesson 1: Hello & Identity',
                description: 'Essential greetings and introducing yourself.',
                timeLimit: null,
                lessons: ['AdultPreBeginnerL1.js'] 
            },
            {
                unitId: 'pre_beg_unit_2',
                title: 'Lesson 2: Food & Drink',
                description: 'Essential food vocabulary and polite requests.',
                timeLimit: null,
                lessons: ['AdultPreBeginnerL2.js'] 
            }
        ]
    },

    // ==========================================
    // 1. KIDS ENGLISH (Level 1)
    // ==========================================
    'kids_beginner': {
        category: 'Kids & Teens',
        title: "Kids English: Foundations (Level 1)",
        description: 'Fun interactive lessons with games, colors, and stories for young learners.',
        image: 'https://placehold.co/400x200/f59e0b/ffffff?text=Kids+English',
        units: Array.from({ length: 16 }, (_, i) => ({
            unitId: `unit_${i + 1}_kids`,
            title: `Lesson ${i + 1}`,
            description: `Kids Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`BeginnerKidsLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "Introductions & Places", "Numbers & Colors", "The Camping Trip", "Home & Family", "A Trip to the Mall",
                "Food Party!", "Weather & Seasons", "My Day & Routine", "Clothes & Fashion", "Around the House",
                "Weather Reporter", "Forest Camping", "Travel & Transport", "The Toy Shop", "Level 1 Capstone",
                "Weird & Wild (Gamer Edition)"
            ];
            return { 
                ...unit, 
                title: `Lesson ${index + 1}: ${titles[index]}`, 
                description: index === 15 ? "A custom gamer-themed lesson with wild vocabulary and epic gear." : "A fun interactive lesson for kids." 
            };
        })
    },

    // ==========================================
    // 2. BEGINNER COURSE (Adults)
    // ==========================================
    'adult_beginner': {
        category: 'General English',
        title: 'Beginner English: Daily Life & Work',
        description: 'Foundational English for the modern workplace and everyday living.',
        image: 'https://placehold.co/400x200/22c55e/ffffff?text=Business+Beginner',
        units: Array.from({ length: 12 }, (_, i) => ({
            unitId: `ab_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Business Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`BeginnerLesson${i + 1}.js`] 
        })).map((unit, index) => {
            const titles = [
                "Locating Belongings", "Ordering Food & Drink", "A Trip to the Zoo", "Registration & Forms", "Business Contacts",
                "Numbers in Business", "Money & Prices", "Equipment & Workspace", "Time & Calendar", "Module 1 Review",
                "Daily Routines", "Parts of the Building"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Essential vocabulary for work and life." };
        })
    },

    // ==========================================
    // 3. ADULT ESL LESSONS
    // ==========================================
    'adult_esl_lessons': {
        category: 'General English',
        title: 'Adult ESL Lessons',
        description: 'Advanced conversational topics, modern dilemmas, idioms, and workplace scenarios for adults.',
        image: 'https://placehold.co/400x200/6366f1/ffffff?text=Adult+ESL',
        units: [
            {
                unitId: 'adult_esl_unit_1',
                title: 'Lesson 1: Modern Life & Idioms',
                description: 'Explore modern workplace issues, idioms, and daily life scenarios.',
                timeLimit: null,
                lessons: ['EslLesson1.js']
            },
            {
                unitId: 'adult_esl_unit_2',
                title: 'Lesson 2: Sydney Travel & Landmarks',
                description: 'Vocabulary and comprehension regarding travel, transport, and landmarks in Sydney.',
                timeLimit: null,
                lessons: ['EslLesson2.js']
            },
            {
                unitId: 'adult_esl_unit_3',
                title: 'Lesson 3: Sydney Travel Roleplay',
                description: 'Interactive conversation practice and roleplay scenarios for traveling in Sydney.',
                timeLimit: null,
                lessons: ['EslLesson3.js']
            },
            {
                unitId: 'adult_esl_unit_4',
                title: 'Lesson 4: Building Structured Learning Goals',
                description: 'A 5-hour module translating educational concepts and building a visual PowerPoint learning plan.',
                timeLimit: 300,
                lessons: ['EslLesson4.js']
            },
            {
                unitId: 'adult_esl_unit_5',
                title: 'Lesson 5: Perfect Tenses in Action',
                description: 'Practice writing and recognizing the Present Perfect and Present Perfect Continuous tenses for completed and ongoing actions.',
                timeLimit: null,
                lessons: ['EslLesson5.js']
            }
        ]
    },

    // ==========================================
    // 4. INTERMEDIATE ESL
    // ==========================================
    'intermediate_esl': { 
        category: 'General English',
        title: 'Intermediate English: Grammar & Narrative',
        description: 'Mastering tenses, structures, and narrative flow.',
        image: 'https://placehold.co/400x200/10b981/ffffff?text=Intermediate+Grammar',
        units: Array.from({ length: 30 }, (_, i) => ({
            unitId: `int_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Intermediate Lesson ${i + 1}`,
            timeLimit: 60,
            lessons: [`IntermediateLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "Past vs. Perfect", "Present Perfect Continuous", "Narrative Tenses", "Past Perfect", "Future Forms 1",
                "Future Forms 2", "State vs. Action Verbs", "Habits & Past States", "Adaptation", "Gerunds vs. Infinitives 1",
                "Gerunds vs. Infinitives 2", "Phrasal Verbs 1", "Modals of Ability & Permission", "Modals of Obligation", "Modals of Deduction",
                "Zero & First Conditional", "Second Conditional", "Third Conditional", "Sentence Structure (SVO)", "Articles & Determinants",
                "Quantifiers", "Relative Clauses 1", "Relative Clauses 2", "Prepositions of Place", "Passive Voice 1",
                "Passive Voice 2", "Reported Speech 1", "Reported Speech 2", "Linking Words", "Final Level Review"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Comprehensive grammar study." };
        })
    },

    // ==========================================
    // 5. ADVANCED ESL
    // ==========================================
    'advanced_esl': {
        category: 'General English',
        title: 'Advanced English: Nuance & Diplomacy',
        description: 'High-level grammar, nuance, and professional communication.',
        image: 'https://placehold.co/400x200/4f46e5/ffffff?text=Advanced+English',
        units: Array.from({ length: 30 }, (_, i) => ({
            unitId: `adv_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Advanced Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`AdvancedLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "Making Conversation", "Future Forms", "Negotiation Mastery", "Travel & Narrative", "Advice & Predictions",
                "Linking & Comparisons", "Interchange Review 1-4", "Interchange Review 5-8", "Emphasis & Style", "Advanced Review",
                "Word Usage & Storytelling", "Expert Grammar Mastery", "Narrative Mastery", "The Art of Negotiation", "Final Proficiency Exam",
                "Ultimate Connectives", "Professional Proficiency", "Nuance & Subtlety", "Critical Thinking", "Mastery of Style",
                "Psychology & Behavior", "Economics & Finance", "Law & Justice", "Science & Innovation", "Medical & Health",
                "Environment & Sustainability", "Politics & Diplomacy", "Art & Literature", "Technology & AI", "Final Course Capstone"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Advanced communication strategies." };
        })
    },

    // ==========================================
    // 6. READING SERIES
    // ==========================================
    'reading_course': {
        category: 'Reading & Writing',
        title: 'Advanced Reading & Critical Thinking',
        description: 'Critical analysis of current events, debate, and logic.',
        image: 'https://placehold.co/400x200/0ea5e9/ffffff?text=Reading+Series',
        units: Array.from({ length: 16 }, (_, i) => ({
            unitId: `read_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Reading Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`ReadingLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "The Ultimate Deception?", "The CO2 Debate", "Your Phone Was Designed to Condition You", "The End of Retirement?", "The Rise of 'Microslop'",
                "Could Actual Robots Take Over?", "Dystopia of a Cashless Society?", "The Deepfake Heist", "When Internet Pranks Turn Criminal", "Whatever Happened to the 4-Day Workweek?",
                "Tech in the Classroom", "The Fast Fashion Crisis", "Lab-Grown Meat", "Case Against Mars Colonization", "The Myth of Multitasking",
                "Writing a Formal Application Letter"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Deep reading and vocabulary." };
        })
    },

    // ==========================================
    // 7. GRAMMAR SERIES (Foundations)
    // ==========================================
    'grammar_series': {
        category: 'Grammar & Drills',
        title: 'Grammar Foundations (A1-A2)',
        description: 'Focused practice on specific foundational grammar points.',
        image: 'https://placehold.co/400x200/ef4444/ffffff?text=Grammar+Drills',
        units: Array.from({ length: 17 }, (_, i) => ({
            unitId: `gram_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Grammar Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`GrammarClass${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "Mixed Tense Masterclass", "Verb 'To Be' & Pronouns", "Present Simple", "Present Continuous", "Past Simple",
                "Basic Past Continuous", "Present Perfect", "Future Forms", "Modal Verbs", "Comparatives & Superlatives",
                "Pronouns", "Object Pronouns & Possessives", "Question Words", "Past Modals of Deduction", "Module 1 Review",
                "Intermediate Past Continuous", "Advanced Practice"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Focused grammar practice." };
        })
    },

    // ==========================================
    // 8. GOOGLE COURSE
    // ==========================================
    'google_course': {
        category: 'Technology & Digital Skills',
        title: 'Google Workspace Mastery',
        description: 'Master Google Workspace tools effectively (Drive, Docs, Sheets, etc).',
        image: 'https://placehold.co/400x200/DB4437/ffffff?text=Google+Course',
        units: Array.from({ length: 8 }, (_, i) => ({
            unitId: `google_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Google Lesson ${i + 1}`,
            timeLimit: 90,
            lessons: [`GoogleCourse${i + 1}.js`]
        })).map((unit, index) => {
            const titles = ["The Organizer", "The Communicator", "The Creator", "The Analyst", "The Scholar", "Google Sites", "The Optimizer", "The Connector"];
            return { ...unit, title: `Module ${index + 1}: ${titles[index]}`, description: "Digital skills mastery." };
        })
    },

    // ==========================================
    // 9. COMPUTER ENGLISH
    // ==========================================
    'computer_english': {
        category: 'Technology & Digital Skills',
        title: 'IT Support & Computer Literacy',
        description: 'Technical vocabulary for IT and digital literacy.',
        image: 'https://placehold.co/400x200/3b82f6/ffffff?text=Tech+English',
        units: Array.from({ length: 6 }, (_, i) => ({
            unitId: `comp_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Computer Lesson ${i + 1}`,
            timeLimit: 90,
            lessons: [`ComputerCourse${i + 1}.js`]
        })).map((unit, index) => {
            const titles = ["Talking Tech", "IT Security & Reporting", "IT Support Masterclass", "File Management & Windows", "Windows 11 & Google Drive", "Cloud Computing Mastery"];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Technical English skills." };
        })
    },

    // ==========================================
    // 10. EVERYDAY DIGITAL SKILLS
    // ==========================================
    'digital_skills': {
        category: 'Technology & Digital Skills',
        title: 'Everyday Digital Skills (Essentials)',
        description: 'Practical computer tasks for daily life: Files, Email, Excel, Calendar & PDFs.',
        image: 'https://placehold.co/400x200/3b82f6/ffffff?text=Digital+Skills',
        units: [
            {
                unitId: 'digital_skills_1',
                title: 'Lesson 1: Digital Essentials',
                description: 'Comprehensive training on File Management, Professional Email, Excel Basics, Calendar Scheduling, and PDF Handling.',
                timeLimit: 120, 
                lessons: ['ComputerCourseLesson1.js']
            },
            {
                unitId: 'digital_skills_2',
                title: 'Lesson 2: Incident Reporting',
                description: 'Hardware, Software, Network, Security, and Helpdesk communication.',
                timeLimit: 120, 
                lessons: ['ComputerCourseLesson2.js']
            },
            {
                unitId: 'digital_skills_3',
                title: 'Lesson 3: Navigating Windows',
                description: 'Describing the Windows interface, menus, settings, and window controls.',
                timeLimit: 120, 
                lessons: ['ComputerCourseLesson3.js']
            }
        ]
    },

    // ==========================================
    // 11. BUSINESS ENGLISH
    // ==========================================
    'business_english': {
        category: 'Business English',
        title: 'Business English: Communication Skills',
        description: 'Professional communication for the workplace.',
        image: 'https://placehold.co/400x200/475569/ffffff?text=Business+English',
        units: Array.from({ length: 8 }, (_, i) => ({
            unitId: `bus_unit_${i + 1}`,
            title: `Lesson ${i + 1}`,
            description: `Business Lesson ${i + 1}`,
            timeLimit: null,
            lessons: [`BusinessLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = ["Identity & First Impressions", "The Daily Grind & Routines", "Office Objects & Technology", "Telephoning Basics", "Advanced Telephoning", "Business Correspondence I", "Correspondence II", "Scheduling & Dates"];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index]}`, description: "Professional communication." };
        })
    },

    // ==========================================
    // 12A. GRAMMAR 1 COURSE (NEW)
    // ==========================================
    'grammar_level1': {
        category: 'Grammar & Drills',
        title: "Grammar Level 1 (A1-A2)",
        description: 'Fundamental grammar lessons featuring simple, relatable everyday stories.',
        image: 'https://placehold.co/400x200/0ea5e9/ffffff?text=Grammar+Level+1', 
        units: [
            { unitId: 'gram1_unit_1', title: 'Unit 1: Basic Indefinite Pronouns', description: 'Learn to use someone, anyone, no one, and nothing in simple sentences.', timeLimit: null, lessons: ['Grammar1Lesson1.js'] },
            { unitId: 'gram1_unit_2', title: 'Unit 2: Connectors & Past Tense', description: 'Simple connectors (so, because) and basic past tense narrative stories.', timeLimit: null, lessons: ['Grammar1Lesson2.js'] },
            { unitId: 'gram1_unit_3', title: 'Unit 3: Intro to Passive Voice', description: 'A basic introduction to the passive voice using the "be" verb.', timeLimit: null, lessons: ['Grammar1Lesson3.js'] },
            { unitId: 'gram1_unit_4', title: 'Unit 4: Made Of vs. Made From', description: 'Learn about simple materials and how everyday things are made.', timeLimit: null, lessons: ['Grammar1Lesson4.js'] },
            { unitId: 'gram1_unit_5', title: 'Unit 5: Story Mastery', description: 'Practice grammar in the context of an everyday, easy-to-read story.', timeLimit: null, lessons: ['Grammar1Lesson5.js'] },
            { unitId: 'gram1_unit_6', title: 'Unit 6: Active vs Passive', description: 'Identify who is doing the action in simple, everyday sentences.', timeLimit: null, lessons: ['Grammar1Lesson6.js'] },
            { unitId: 'gram1_unit_7', title: 'Unit 7: Passive Voice in Action', description: 'Use the passive voice to describe everyday objects and situations.', timeLimit: null, lessons: ['Grammar1Lesson7.js'] },
            { unitId: 'gram1_unit_8', title: 'Unit 8: Grammar Roleplay', description: 'A fun, simple roleplay focusing on basic question forms and past tense.', timeLimit: null, lessons: ['Grammar1Lesson8.js'] },
            { unitId: 'gram1_unit_9', title: 'Unit 9: Getting Things Done', description: 'Basic introduction to the causative form (e.g., having a haircut, getting a car fixed).', timeLimit: null, lessons: ['Grammar1Lesson9.js'] },
            { unitId: 'gram1_unit_10', title: 'Unit 10: Simple Present Routine', description: 'Describe daily routines and habits using both active and passive voice.', timeLimit: null, lessons: ['Grammar1Lesson10.js'] }
        ]
    },

    // ==========================================
    // 12B. GRAMMAR 2 COURSE
    // ==========================================
    'grammar_level2': {
        category: 'Grammar & Drills',
        title: "Grammar Deep-Dive (B1-B2)",
        description: 'Intermediate grammar rules with in-depth practice exercises.',
        image: 'https://placehold.co/400x200/8b5cf6/ffffff?text=Grammar+Level+2', 
        units: [
            { unitId: 'gram2_unit_1', title: 'Unit 1: Indefinite Pronouns', description: 'Usage rules for anyone, no one, anything, nothing.', timeLimit: null, lessons: ['Grammar2Lesson1.js'] },
            { unitId: 'gram2_unit_2', title: 'Unit 2: Connectors & Past Perfect', description: 'Using so/so that, reported speech, and past perfect tense.', timeLimit: null, lessons: ['Grammar2Lesson2.js'] },
            { unitId: 'gram2_unit_3', title: 'Unit 3: Passive Voice', description: 'Mastering the passive voice structure and usage.', timeLimit: null, lessons: ['Grammar2Lesson3.js'] },
            { unitId: 'gram2_unit_4', title: 'Unit 4: Passive Voice - Made', description: 'Mastering "made of" vs "made from", causative forms, and idioms.', timeLimit: null, lessons: ['Grammar2Lesson4.js'] },
            { unitId: 'gram2_unit_5', title: 'Unit 5: Story Mastery - "The Heist"', description: 'Using made for/by/from/to/up in a narrative context.', timeLimit: null, lessons: ['Grammar2Lesson5.js'] },
            { unitId: 'gram2_unit_6', title: 'Unit 6: Active vs. Passive Voice', description: 'Role-play scenarios practicing Active vs Passive Voice.', timeLimit: null, lessons: ['Grammar2Lesson6.js'] },
            { unitId: 'gram2_unit_7', title: 'Unit 7: Passive Voice Rescue', description: 'Reinforcement of Present, Past, and Future Passive voice with a Detective theme.', timeLimit: null, lessons: ['Grammar2Lesson7.js'] },
            { unitId: 'gram2_unit_8', title: 'Unit 8: The Massive Passive Roleplay', description: 'A three-act detective mystery practicing context switching between Active and Passive voice.', timeLimit: null, lessons: ['Grammar2Lesson8.js'] },
            { unitId: 'gram2_unit_9', title: 'Unit 9: The Causative', description: 'Services and Arranging tasks (Have/Get something done).', timeLimit: null, lessons: ['Grammar2Lesson9.js'] },
            { unitId: 'gram2_unit_10', title: 'Unit 10: Simple Present Tense', description: 'Mastering Active vs. Passive Voice in the Simple Present Tense.', timeLimit: null, lessons: ['Grammar2Lesson10.js'] },
            { unitId: 'gram2_unit_11', title: 'Unit 11: Present Continuous Tense', description: 'Mastering Active vs. Passive Voice in the Present Continuous Tense.', timeLimit: null, lessons: ['Grammar2Lesson11.js'] },
            { unitId: 'gram2_unit_12', title: 'Unit 12: Simple Past Tense', description: 'Mastering Active vs. Passive Voice in the Simple Past Tense.', timeLimit: null, lessons: ['Grammar2Lesson12.js'] },
            { unitId: 'gram2_unit_13', title: 'Unit 13: Past Continuous Tense', description: 'Mastering Active vs. Passive Voice in the Past Continuous Tense.', timeLimit: null, lessons: ['Grammar2Lesson13.js'] },
            { unitId: 'gram2_unit_14', title: 'Unit 14: Present Perfect Tense', description: 'Mastering Active vs. Passive Voice in the Present Perfect Tense.', timeLimit: null, lessons: ['Grammar2Lesson14.js'] },
            { unitId: 'gram2_unit_15', title: 'Unit 15: Past Perfect Tense', description: 'Mastering Active vs. Passive Voice in the Past Perfect Tense.', timeLimit: null, lessons: ['Grammar2Lesson15.js'] },
            { unitId: 'gram2_unit_16', title: 'Unit 16: Simple Future Tense', description: 'Mastering Active vs. Passive Voice in the Simple Future Tense.', timeLimit: null, lessons: ['Grammar2Lesson16.js'] },
            { unitId: 'gram2_unit_17', title: 'Unit 17: Be Going To (Future)', description: 'Mastering Active vs. Passive Voice using Be Going To.', timeLimit: null, lessons: ['Grammar2Lesson17.js'] },
            { unitId: 'gram2_unit_18', title: 'Unit 18: Modal Verbs', description: 'Mastering Active vs. Passive Voice with Modal Verbs.', timeLimit: null, lessons: ['Grammar2Lesson18.js'] },
            { unitId: 'gram2_unit_19', title: 'Unit 19: Roleplay Dialogues', description: 'Practicing tense switching, prepositions, and articles through conversational roleplays.', timeLimit: null, lessons: ['Grammar2Lesson19.js'] },
            { unitId: 'gram2_unit_20', title: 'Unit 20: The Discovery of Nova-9', description: 'Comprehensive review of Reported Speech, Passive Voice, and Mixed Tenses.', timeLimit: null, lessons: ['Grammar2Lesson20.js']},
            { unitId: 'gram2_unit_21', title: 'Unit 21: The Museum Heist', description: 'Comprehensive review of Reported Speech and Passives in a Detective Mystery theme.', timeLimit: null, lessons: ['Grammar2Lesson21.js']},
            { unitId: 'gram2_unit_22', title: 'Unit 22: Mastery Challenge', description: 'Comprehensive 60-question review of Active, Passive, and Reported Speech.', timeLimit: null, lessons: ['Grammar2Lesson22.js']},
            { unitId: 'gram2_unit_23', title: 'Unit 23: Comprehensive Review', description: 'Review of vocabulary, sentence patterns, tenses, and reported questions.', timeLimit: null, lessons: ['Grammar2Lesson23.js']},
            { unitId: 'gram2_unit_24', title: 'Unit 24: Vocabulary Decoding Masterclass', description: 'Learn to use context clues and word parts to decode unknown vocabulary.', timeLimit: null, lessons: ['Grammar2Lesson24.js']},
            { unitId: 'gram2_unit_25', title: 'Unit 25: Vocabulary & Grammar Mastery', description: 'Identify the correct grammatical usage of advanced vocabulary in various tenses and forms.', timeLimit: null, lessons: ['Grammar2Lesson25.js']},
            { unitId: 'gram2_unit_26', title: 'Unit 26: The Detective\'s Timeline', description: 'Master the past tenses (Simple, Continuous, Perfect) using a detective mystery theme.', timeLimit: null, lessons: ['Grammar2Lesson26.js']},
            { unitId: 'gram2_unit_27', title: 'Unit 27: Finding Simple Meanings', description: 'Match advanced vocabulary sentences with their simpler, everyday equivalents.', timeLimit: null, lessons: ['Grammar2Lesson27.js']},
            { unitId: 'gram2_unit_28', title: 'Unit 28: The Ultimate Mock Exam', description: 'Comprehensive mock exam testing advanced vocabulary and all Module 2 grammar points.', timeLimit: null, lessons: ['Grammar2Lesson28.js']},
            { unitId: 'gram2_unit_29', title: 'Unit 29: Infinitives & Gerunds', description: 'Learn when to use bare infinitives, to-infinitives, and gerunds through the interactive story of Mia and Leo at the Travel Agency.', timeLimit: null, lessons: ['Grammar2Lesson29.js']},
            { unitId: 'gram2_unit_30', title: 'Unit 30: The Ultimate 60-Question Marathon', description: 'No tabs. No breaks. 60 straight questions to prove absolute mastery over this module. Good luck!', timeLimit: null, lessons: ['Grammar2Lesson30.js']},
            { unitId: 'gram2_unit_31', title: 'Unit 31: "Switch-Hitter" Verbs', description: 'Learn how using a gerund vs. an infinitive can completely change the meaning of your sentence through the high-pressure world of a restaurant kitchen.', timeLimit: null, lessons: ['Grammar2Lesson31.js']},
            { unitId: 'gram2_unit_32', title: 'Unit 32: Mastery of Switch-Hitter Verbs', description: 'Extensive 60-question practice on advanced gerund/infinitive nuances and perception verbs set in a luxury hotel.', timeLimit: null, lessons: ['Grammar2Lesson32.js']},
            { unitId: 'gram2_unit_33', title: 'Unit 33: Causative Services & Parallel Choices', description: 'Master how to talk about tasks performed by others using have/get something done, and ensure precise parallel structure.', timeLimit: null, lessons: ['Grammar2Lesson33.js']},
            { unitId: 'gram2_unit_34', title: 'Unit 34: Environmental & Social Problems', description: 'Learn to discuss city issues and global challenges using the passive with prepositions and propose solutions using infinitive clauses.', timeLimit: null, lessons: ['Grammar2Lesson34.js']},
            { unitId: 'gram2_unit_35', title: 'Unit 35: Ultimate Grammar Review', description: 'Master the complexities of the Passive Voice and Gerunds vs. Infinitives in this comprehensive 60-question school-themed challenge.', timeLimit: null, lessons: ['Grammar2Lesson35.js']},
            { unitId: 'gram2_unit_36', title: 'Unit 36: Grammar Class 36 Final Exam', description: 'Comprehensive 90-question review of tenses, passive voice, gerunds, infinitives, vocabulary, and connectives.', timeLimit: null, lessons: ['Grammar2Lesson36.js']},
            { unitId: 'gram2_unit_37', title: 'Unit 37: Sentence-Making & Pattern Mastery', description: 'Master essential sentence patterns and gerunds as actions through interactive sentence building.', timeLimit: null, lessons: ['Grammar2Lesson37.js']},
            { unitId: 'gram2_unit_38', title: 'Unit 38: Formal Letters: Address Formatting', description: 'Learn the correct order and placement of addresses for a formal application letter.', timeLimit: null, lessons: ['Grammar2Lesson38.js']},
            { unitId: 'gram2_unit_39', title: 'Unit 39: Exam Prep (Cloze & Comprehension)', description: 'Master IELTS-style True/False/Not Given reading comprehension and open cloze context deduction without word banks.', timeLimit: null, lessons: ['Grammar2Lesson39.js'] }
        ]
    },

    // ==========================================
    // 13. NUMERACY IN ENGLISH
    // ==========================================
    'numeracy_english': {
        category: 'Numeracy & Everyday Math',
        title: "Numeracy in English",
        description: 'Learn how to confidently talk about numbers, prices, measurements, and data in everyday English.',
        image: 'https://placehold.co/400x200/10b981/ffffff?text=Numeracy+English', 
        units: [
            { unitId: 'num_eng_unit_1', title: 'Lesson 1: The ATM Limit', description: 'Interactive roleplay and listening comprehension covering bank transactions, withdrawal limits, and surcharges.', timeLimit: null, lessons: ['NumeracyLesson1.js'] },
            { unitId: 'num_eng_unit_2', title: 'Lesson 2: The Holiday Budget', description: 'Handling hotel deposits, currency, calculating change, and schedules.', timeLimit: null, lessons: ['NumeracyLesson2.js'] },
            { unitId: 'num_eng_unit_3', title: 'Lesson 3: The Car Hunt', description: 'Negotiating prices, comparing costs, and understanding everyday marketplace language.', timeLimit: null, lessons: ['NumeracyLesson3.js'] },
            { unitId: 'num_eng_unit_4', title: 'Lesson 4: Fractions & Percentages', description: 'Talking about proportions, sales, and parts of a whole.', timeLimit: null, lessons: ['NumeracyLesson4.js'] },
            { unitId: 'num_eng_unit_5', title: 'Lesson 5: Charts & Data Trends', description: 'Describing basic graphs, increases, decreases, and averages.', timeLimit: null, lessons: ['NumeracyLesson5.js'] }
        ]
    },

    // ==========================================
    // 14. READING COMPANION DRILLS
    // ==========================================
    'reading_drills': {
        category: 'Reading & Writing',
        title: "Reading Companion Drills",
        description: 'Printable worksheets for reading repetition and grammar fluency.',
        image: 'https://placehold.co/400x200/0d9488/ffffff?text=Reading+Drills', 
        units: [
            { unitId: 'drill_unit_1', title: 'Drill 1: Indefinite Pronouns', description: 'Drills for "The Ultimate Deception" (Moon Landing)', timeLimit: null, lessons: ['ReadingDrillLesson1.js'] },
            { unitId: 'drill_unit_2', title: 'Drill 2: Contrast & Concession', description: 'Drills for "Climate Change" (Despite/Although)', timeLimit: null, lessons: ['ReadingDrillLesson2.js'] }
        ]
    },

    // ==========================================
    // 15. MICROSOFT COURSE
    // ==========================================
    'microsoft_course': {
        category: 'Technology & Digital Skills',
        title: "Microsoft Office 365 Mastery",
        description: 'Comprehensive training on Microsoft Office 365 and workplace productivity.',
        image: 'https://placehold.co/400x200/0078d4/ffffff?text=Microsoft+365', 
        units: Array.from({ length: 30 }, (_, i) => ({
            unitId: `ms_unit_${i + 1}`,
            title: `Worksheet ${i + 1}`,
            description: `Microsoft Skills Worksheet ${i + 1}`,
            timeLimit: null,
            lessons: [`MicrosoftLesson${i + 1}.js`]
        })).map((unit, index) => {
            const titles = [
                "Introduction to Word", "Word Masterclass", "Layouts & Objects", "Advanced Styling", "Word Review",
                "Microsoft Word Final Exam", "Introduction to Excel", "Excel: Formulas & Formatting", "Excel Formulas & Data", "Charts & Graphs",
                "Data Management", "Excel Pivot Tables", "PowerPoint Slides", "Design & Transitions", "Animations & Media",
                "Presenting Successfully", "Outlook Email Management", "Calendar & Scheduling", "OneNote for Organization", "OneDrive Cloud Storage",
                "Teams Collaboration", "Teams Meetings", "SharePoint Essentials", "Microsoft Forms", "Planner & Tasks",
                "Introduction to Access", "Advanced Word Features", "Advanced Excel Functions", "PowerPoint Master Slides", "Security & Permissions"
            ];
            return { ...unit, title: `Lesson ${index + 1}: ${titles[index] || "Bonus Lesson"}`, description: "Professional Microsoft skills training." };
        })
    },

    // ==========================================
    // 16. NAPLAN WRITING COURSE: YEAR 7
    // ==========================================
    'naplan_writing_year7': {
        category: 'Exam Preparation',
        title: "NAPLAN Writing Prep: Year 7",
        description: 'Mastering narrative, persuasive, and informative writing structures, and Year 7 techniques.',
        image: 'https://placehold.co/400x200/f43f5e/ffffff?text=NAPLAN+Year+7', 
        units: [
            { unitId: 'naplan_y7_unit_1', title: 'Test 1 (Narrative): The Discovery', description: 'Full 40-minute creative writing test featuring a narrative prompt about a discovery.', timeLimit: 40, lessons: ['NaplanYear7Writing1.js'] },
            { unitId: 'naplan_y7_unit_2', title: 'Test 2 (Persuasive): Rules Are Made To Be Broken', description: 'Full 40-minute writing test featuring a persuasive prompt on rules.', timeLimit: 40, lessons: ['NaplanYear7Writing2.js'] },
            { unitId: 'naplan_y7_unit_3', title: 'Test 3 (Informative): The Power of Sleep', description: 'Full 40-minute writing test featuring an informative/explanatory prompt about sleep.', timeLimit: 40, lessons: ['NaplanYear7Writing3.js'] },
            { unitId: 'naplan_y7_unit_4', title: 'Test 4 (Informative): The Future of Energy', description: 'Full 40-minute writing test featuring an informative prompt about renewable energy.', timeLimit: 40, lessons: ['NaplanYear7Writing4.js'] },
            { unitId: 'naplan_y7_unit_5', title: 'Test 5: Sample High-Scoring Responses', description: 'Review high-level narrative and persuasive sample essays.', timeLimit: null, lessons: ['NaplanYear7Samples.js'] }
        ]
    },

    // ==========================================
    // 17. GRAMMAR PATTERN DRILLS
    // ==========================================
    'grammar_patterns': {
        category: 'Grammar & Drills',
        title: "Grammar Pattern Drills",
        description: 'Read, Repeat, and Master essential grammar structures based on the textbook.',
        image: 'https://placehold.co/400x200/0d9488/ffffff?text=Pattern+Drills', 
        units: [
            { unitId: 'pattern_unit_1', title: 'Unit 1: Indefinite Pronouns', description: 'Textbook Unit 1: Pronouns', timeLimit: null, lessons: ['PatternDrillLesson1.js'] },
            { unitId: 'pattern_unit_2', title: 'Unit 1: Noun Phrases', description: 'Textbook Unit 1: Noun Phrases', timeLimit: null, lessons: ['PatternDrillLesson2.js'] }
        ]
    },

    // ==========================================
    // 18. NAPLAN WRITING COURSE: YEAR 5
    // ==========================================
    'naplan_writing_year5': {
        category: 'Exam Preparation',
        title: "NAPLAN Writing Prep: Year 5",
        description: 'Mastering basic narrative structure and persuasive opinions for Year 5.',
        image: 'https://placehold.co/400x200/f59e0b/ffffff?text=NAPLAN+Year+5', 
        units: [
            { unitId: 'naplan_y5_unit_1', title: 'Test 1 (Narrative/Persuasive): The Key & Too Safe?', description: 'Full 40-minute writing test focusing on Beginning/Middle/End and 3 distinct reasons.', timeLimit: 40, lessons: ['NaplanYear5Writing1.js'] }
        ]
    },

    // ==========================================
    // 19. ADVENTURE SERIES (Kids English)
    // ==========================================
    'adventure_series': {
        category: 'Kids & Teens',
        title: "Kids English: The Adventure Series",
        description: 'An action-packed English journey through jungles, space, and under the sea.',
        image: 'https://placehold.co/400x200/0ea5e9/ffffff?text=Adventure+Series', 
        units: [
            {
                unitId: 'adv_kids_unit_1',
                title: 'Lesson 1: Greetings & The Verb Be',
                description: 'A 1-hour roleplay adventure navigating a thick jungle to find treasure.',
                timeLimit: 60,
                lessons: ['AdventureKidsLesson1.js']
            },
            {
                unitId: 'adv_kids_unit_2',
                title: 'Lesson 2: Numbers & Colors',
                description: 'A space-themed exploration focusing on technology and action verbs.',
                timeLimit: 60,
                lessons: ['AdventureKidsLesson2.js']
            },
            {
                unitId: 'adv_kids_unit_3',
                title: 'Lesson 3: Deep Sea Treasure',
                description: 'Searching the ocean floor while practicing prepositions and marine life vocabulary.',
                timeLimit: 60,
                lessons: ['AdventureKidsLesson3.js']
            },
            {
                unitId: 'adv_kids_unit_4',
                title: 'Lesson 4: The Dragon\'s Castle',
                description: 'Describing fantasy creatures and settings in a medieval roleplay.',
                timeLimit: 60,
                lessons: ['AdventureKidsLesson4.js']
            },
            {
                unitId: 'adv_kids_unit_5',
                title: 'Lesson 5: Island Survival',
                description: 'Practicing survival skills and modal verbs on a desert island.',
                timeLimit: 60,
                lessons: ['AdventureKidsLesson5.js']
            }
        ]
    },

    // ==========================================
    // 20. NAPLAN READING COURSE: YEAR 7
    // ==========================================
    'naplan_reading_year7': {
        category: 'Exam Preparation',
        title: "NAPLAN Reading Prep: Year 7",
        description: 'Practice reading comprehension, interpreting texts, and analyzing language for Year 7 level.',
        image: 'https://placehold.co/400x200/8b5cf6/ffffff?text=NAPLAN+Reading+Y7', 
        units: [
            { 
                unitId: 'naplan_y7_read_unit_1', 
                title: 'Test 1: Reading Comprehension', 
                description: 'Full 45-minute reading test featuring various text types and comprehension questions.', 
                timeLimit: 45, 
                lessons: ['NaplanYear7Reading1.js'] 
            }
        ]
    },

    // ==========================================
    // 21. COMPUTER PROJECTS
    // ==========================================
    'computer_projects': {
        category: 'Technology & Digital Skills',
        title: "Computer Projects",
        description: 'Hands-on digital projects integrating ESL skills with computer literacy.',
        image: 'https://placehold.co/400x200/2563eb/ffffff?text=Computer+Projects', 
        units: [
            { 
                unitId: 'comp_proj_unit_1', 
                title: 'Project 1: My Home, My Heritage', 
                description: 'Create a digital wall poster about your home country using research and design skills.', 
                timeLimit: null, 
                lessons: ['ComputerProjects1.js'] 
            },
            { 
                unitId: 'comp_proj_unit_2', 
                title: 'Project 2: Smart Savings with AI', 
                description: 'Use Google Gemini to research and create an information sheet about the cheapest fuel prices in Sydney.', 
                timeLimit: 180, 
                lessons: ['ComputerProjects2.js'] 
            },
            { 
                unitId: 'comp_proj_unit_3', 
                title: 'Project 3: Build a Professional Website', 
                description: 'Learn web design basics using Wix to create and publish a free 3-page personal portfolio.', 
                timeLimit: 240, 
                lessons: ['ComputerProjects3.js'] 
            },
            { 
                unitId: 'comp_proj_unit_4', 
                title: 'Project 4: The AI Business Pitch', 
                description: 'Act as an entrepreneur to brainstorm, brand, and pitch a new business idea for the Sydney market using Gemini AI.', 
                timeLimit: 180, 
                lessons: ['ComputerProjects4.js'] 
            },
            { 
                unitId: 'comp_proj_unit_5', 
                title: 'Project 5: The AI Director', 
                description: 'Use AI tools to write a script, generate a voiceover, create visuals, and export a 60-second video.', 
                timeLimit: 180, 
                lessons: ['ComputerProjects5.js'] 
            }
        ]
    },

    // ==========================================
    // 22. JOB SKILLS (WORKSHOPS & SERIES)
    // ==========================================
    'job_skills': {
        category: 'Business English',
        title: 'Job Skills & Career Prep',
        description: 'A comprehensive module featuring a deep-dive 4-part series and a 1-hour fast-paced masterclass.',
        image: 'https://placehold.co/400x200/f97316/ffffff?text=Job+Skills',
        units: [
            {
                unitId: 'job_skills_series_1',
                title: 'Lesson 1: Resume Design & Formatting',
                description: 'Master the golden rules of resume layouts and build your professional base document.',
                timeLimit: 60,
                lessons: ['JobSearchLesson1.js']
            },
            {
                unitId: 'job_skills_series_2',
                title: 'Lesson 2: Decoding the Job Market',
                description: 'Understand hard vs. soft skills, search SEEK categories, and perform a skills gap analysis.',
                timeLimit: 60,
                lessons: ['JobSearchLesson2.js']
            },
            {
                unitId: 'job_skills_series_3',
                title: 'Lesson 3: Tailoring Your Application',
                description: 'Learn how to connect your resume to specific job ads and prepare for the interview.',
                timeLimit: 60,
                lessons: ['JobSearchLesson3.js']
            },
            {
                unitId: 'job_skills_series_4',
                title: 'Lesson 4: The Job Search Project',
                description: 'A capstone project applying all skills to a real-world job application scenario.',
                timeLimit: 60,
                lessons: ['JobSearchLesson4.js']
            },
            {
                unitId: 'job_skills_workshop',
                title: '1-Hour Masterclass: Master Resume Creation',
                description: 'A fast-paced, 60-minute workshop on creating a master resume and formatting rules.',
                timeLimit: 60,
                lessons: ['JobSearchWorkshop.js']
            }
        ]
    }
};

export const getAllLessons = () => {
    const lessons = [];
    Object.entries(COURSE_UNITS_DATA).forEach(([courseId, course]) => {
        course.units.forEach(unit => {
            unit.lessons.forEach(lessonFile => {
                lessons.push({
                    id: lessonFile.replace('.js', ''), 
                    fileName: lessonFile,
                    label: `${course.title} - ${unit.title}`
                });
            });
        });
    });
    return lessons;
};